import { useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import HistoryIcon from '@mui/icons-material/History'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import { useSnackbar } from 'notistack'

import { useSettingAccessUserPageContextContext } from '../../../../../pages/Setting/Access/User'
import { useGetActiveShippingCompanies } from '../../../../../hook/query/useShippingMaster'
import { useGetWarehouseMasters } from '../../../../../hook/query/useWarehouseMaster'
import {
  useCreateTransportPlannerScope,
  useGetTransportPlannerScopeAudits,
  useGetTransportPlannerScopes,
  useRemoveTransportPlannerScope,
  useUpdateTransportPlannerScope,
} from '../../../../../hook/query/useTransportPlannerScope'
import {
  TransportPlannerScope,
  TransportPlannerScopeStatus,
} from '../../../../../compiler/transportPlannerScope.interface'

type ScopeAction =
  | { mode: 'activate' | 'deactivate' | 'remove'; scope: TransportPlannerScope }
  | null

const statusLabel: Record<TransportPlannerScopeStatus, string> = {
  active: 'ใช้งานอยู่',
  inactive: 'ยังไม่เปิดใช้',
  scheduled: 'รอวันเริ่ม',
  expired: 'หมดอายุ',
  removed: 'ยกเลิกแล้ว',
}

const statusColor: Record<TransportPlannerScopeStatus, 'success' | 'default' | 'info' | 'warning'> = {
  active: 'success',
  inactive: 'default',
  scheduled: 'info',
  expired: 'warning',
  removed: 'default',
}

const errorMessage = (error: unknown) => {
  const candidate = error as { response?: { data?: { message?: string } }; message?: string }
  return candidate.response?.data?.message || candidate.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่'
}

export default function TransportManagerDcScopeManage() {
  const { uuid } = useSettingAccessUserPageContextContext()
  const { enqueueSnackbar } = useSnackbar()
  const [createOpen, setCreateOpen] = useState(false)
  const [shippingUuid, setShippingUuid] = useState('')
  const [warehouseUuid, setWarehouseUuid] = useState('')
  const [createReason, setCreateReason] = useState('')
  const [scopeAction, setScopeAction] = useState<ScopeAction>(null)
  const [actionReason, setActionReason] = useState('')
  const [auditScopeUuid, setAuditScopeUuid] = useState<string | null>(null)

  const scopesQuery = useGetTransportPlannerScopes(uuid)
  const shippingQuery = useGetActiveShippingCompanies()
  const warehouseQuery = useGetWarehouseMasters()
  const createScope = useCreateTransportPlannerScope(uuid)
  const updateScope = useUpdateTransportPlannerScope(uuid)
  const removeScope = useRemoveTransportPlannerScope(uuid)
  const auditsQuery = useGetTransportPlannerScopeAudits(auditScopeUuid)
  const scopes = useMemo(() => scopesQuery.data?.data.items || [], [scopesQuery.data])

  const shippingMap = useMemo(
    () => new Map((shippingQuery.data || []).map((item) => [item.uuid, item.company_name])),
    [shippingQuery.data],
  )
  const warehouseMap = useMemo(
    () =>
      new Map(
        (warehouseQuery.data || []).map((item) => [
          item.uuid,
          `${item.code} - ${item.name_th || item.name_en || item.code}`,
        ]),
      ),
    [warehouseQuery.data],
  )
  const warehouseOptions = useMemo(() => {
    const existingWarehouseUuids = new Set(
      scopes
        .filter(
          (scope) =>
            scope.status !== 'removed' && scope.shipping_uuid === shippingUuid,
        )
        .map((scope) => scope.warehouse_uuid),
    )

    return (warehouseQuery.data || [])
      .filter(
        (item) =>
          item.is_active === 1 &&
          item.status === 1 &&
          !existingWarehouseUuids.has(item.uuid),
      )
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
  }, [scopes, shippingUuid, warehouseQuery.data])
  const isBusy = createScope.isLoading || updateScope.isLoading || removeScope.isLoading

  const closeCreate = () => {
    if (createScope.isLoading) return
    setCreateOpen(false)
    setShippingUuid('')
    setWarehouseUuid('')
    setCreateReason('')
  }

  const handleCreate = async () => {
    if (!shippingUuid || !warehouseUuid || createReason.trim().length < 3) return
    try {
      const result = await createScope.mutateAsync({
        shipping_uuid: shippingUuid,
        warehouse_uuid: warehouseUuid,
        reason: createReason.trim(),
      })
      enqueueSnackbar(result.message || 'สร้าง Transport Scope สำเร็จ', { variant: 'success' })
      closeCreate()
    } catch (error) {
      enqueueSnackbar(errorMessage(error), { variant: 'error' })
    }
  }

  const openAction = (mode: 'activate' | 'deactivate' | 'remove', scope: TransportPlannerScope) => {
    setScopeAction({ mode, scope })
    setActionReason('')
  }

  const handleScopeAction = async () => {
    if (!scopeAction || actionReason.trim().length < 3) return
    try {
      const result =
        scopeAction.mode === 'remove'
          ? await removeScope.mutateAsync({
              scopeUuid: scopeAction.scope.uuid,
              payload: { version: scopeAction.scope.version, reason: actionReason.trim() },
            })
          : await updateScope.mutateAsync({
              scopeUuid: scopeAction.scope.uuid,
              payload: {
                version: scopeAction.scope.version,
                is_active: scopeAction.mode === 'activate',
                reason: actionReason.trim(),
              },
            })
      enqueueSnackbar(result.message || 'อัปเดต Transport Scope สำเร็จ', { variant: 'success' })
      setScopeAction(null)
      setActionReason('')
    } catch (error) {
      enqueueSnackbar(errorMessage(error), { variant: 'error' })
    }
  }

  if (!uuid) {
    return (
      <Stack height="100%" justifyContent="center" alignItems="center" p={3}>
        <Alert severity="info">บันทึกข้อมูลผู้ใช้งานก่อน จึงจะกำหนด Transport Scope ได้</Alert>
      </Stack>
    )
  }

  return (
    <Stack height="100%">
      {(scopesQuery.isLoading || scopesQuery.isFetching) && <LinearProgress />}
      <Stack flex={1} overflow="auto" spacing={2} p={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: 'primary.50',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalShippingIcon />
            </Box>
            <Box>
              <Typography fontWeight={700}>Transport Scope</Typography>
              <Typography variant="caption" color="text.secondary">
                กำหนดเป็นคู่บริษัทขนส่ง + DC ผู้ใช้จะเห็นรถ สายส่ง และคนขับเฉพาะขอบเขตนี้
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            เพิ่มขอบเขต
          </Button>
        </Stack>

        <Alert severity="info">
          Scope ใหม่จะถูกสร้างแบบ “ยังไม่เปิดใช้” ก่อน เมื่อเปิดใช้ระบบจะตรวจว่าผู้ใช้มีสิทธิ์
          ข้อมูลสายส่งหรือสถานะขนส่งจาก User/Group แล้ว
        </Alert>
        {scopesQuery.isError && <Alert severity="error">โหลด Transport Scope ไม่สำเร็จ</Alert>}

        {scopes.length === 0 && !scopesQuery.isLoading ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <LocalShippingIcon color="disabled" sx={{ fontSize: 44 }} />
            <Typography fontWeight={700}>ยังไม่มี Transport Scope</Typography>
            <Typography variant="body2" color="text.secondary">
              เพิ่มคู่บริษัทขนส่งและ DC ที่ Planner คนนี้รับผิดชอบ
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {scopes.map((scope) => (
              <Paper key={scope.uuid} variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                  <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography fontWeight={700}>
                        {shippingMap.get(scope.shipping_uuid) || scope.shipping_uuid}
                      </Typography>
                      <Chip
                        size="small"
                        color={statusColor[scope.status]}
                        label={statusLabel[scope.status]}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {warehouseMap.get(scope.warehouse_uuid) || `${scope.dc_code} - ไม่พบใน Warehouse Master`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      เหตุผลล่าสุด: {scope.assignment_reason || '-'} · version {scope.version}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="ประวัติการเปลี่ยนแปลง">
                      <IconButton onClick={() => setAuditScopeUuid(scope.uuid)}>
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                    {!['removed', 'expired'].includes(scope.status) && (
                      <Tooltip title={scope.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}>
                        <IconButton
                          color={scope.is_active ? 'warning' : 'success'}
                          onClick={() => openAction(scope.is_active ? 'deactivate' : 'activate', scope)}
                        >
                          <PowerSettingsNewIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {scope.status !== 'removed' && (
                      <Tooltip title="ยกเลิก Scope">
                        <IconButton color="error" onClick={() => openAction('remove', scope)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>

      <Dialog open={createOpen} onClose={closeCreate} maxWidth="sm" fullWidth>
        <DialogTitle>เพิ่ม Transport Scope</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} pt={0.5}>
            <Autocomplete
              options={shippingQuery.data || []}
              loading={shippingQuery.isLoading}
              getOptionLabel={(option) => option.company_name}
              value={(shippingQuery.data || []).find((item) => item.uuid === shippingUuid) || null}
              onChange={(_, option) => {
                setShippingUuid(option?.uuid || '')
                setWarehouseUuid('')
              }}
              renderInput={(params) => <TextField {...params} label="บริษัทขนส่ง" required />}
            />
            <Autocomplete
              options={warehouseOptions}
              disabled={!shippingUuid}
              loading={warehouseQuery.isLoading}
              getOptionLabel={(option) => `${option.code} - ${option.name_th || option.name_en || option.code}`}
              value={warehouseOptions.find((item) => item.uuid === warehouseUuid) || null}
              onChange={(_, option) => setWarehouseUuid(option?.uuid || '')}
              renderInput={(params) => <TextField {...params} label="DC / คลังสินค้า" required />}
            />
            <TextField
              label="เหตุผล"
              value={createReason}
              onChange={(event) => setCreateReason(event.target.value)}
              helperText="อย่างน้อย 3 ตัวอักษร และจะถูกบันทึกใน Audit"
              inputProps={{ maxLength: 500 }}
              multiline
              minRows={2}
              required
            />
            <Alert severity="warning">หลังเพิ่มแล้วต้องกดเปิดใช้งานอีกครั้ง จึงจะเริ่มจำกัดข้อมูลจริง</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate} disabled={createScope.isLoading}>ยกเลิก</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              !shippingUuid || !warehouseUuid || createReason.trim().length < 3 || createScope.isLoading
            }
          >
            {createScope.isLoading ? <CircularProgress size={20} /> : 'เพิ่ม Scope'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!scopeAction} onClose={() => !isBusy && setScopeAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {scopeAction?.mode === 'activate'
            ? 'เปิดใช้งาน Transport Scope'
            : scopeAction?.mode === 'deactivate'
            ? 'ปิดใช้งาน Transport Scope'
            : 'ยกเลิก Transport Scope'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} pt={0.5}>
            <Alert severity={scopeAction?.mode === 'remove' ? 'error' : 'warning'}>
              การลด Scope มีผลกับ request ถัดไป และอาจทำให้ Planner มองไม่เห็นใบงานเดิมทันที
            </Alert>
            <TextField
              label="เหตุผล"
              value={actionReason}
              onChange={(event) => setActionReason(event.target.value)}
              helperText="อย่างน้อย 3 ตัวอักษร"
              inputProps={{ maxLength: 500 }}
              multiline
              minRows={2}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScopeAction(null)} disabled={isBusy}>ยกเลิก</Button>
          <Button
            color={scopeAction?.mode === 'remove' ? 'error' : 'primary'}
            variant="contained"
            onClick={handleScopeAction}
            disabled={actionReason.trim().length < 3 || isBusy}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!auditScopeUuid} onClose={() => setAuditScopeUuid(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ประวัติ Transport Scope</DialogTitle>
        <DialogContent dividers>
          {auditsQuery.isLoading ? (
            <LinearProgress />
          ) : auditsQuery.data?.data.items.length ? (
            <Stack divider={<Divider flexItem />}>
              {auditsQuery.data.data.items.map((item) => (
                <Box key={item.uuid} py={1.25}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Chip size="small" label={item.action} variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(item.created_at).toLocaleString('th-TH')}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" mt={0.75}>{item.reason}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">ยังไม่มีประวัติ</Typography>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setAuditScopeUuid(null)}>ปิด</Button></DialogActions>
      </Dialog>
    </Stack>
  )
}
